/**
 * PURPOSE: "Publicación en Hayllis 2.0" del Atletismo de un evento.
 * RESPONSIBILITIES: Editar y guardar `Settings` (`GET/PUT .../settings`): mostrar la pestaña en
 *   Hayllis 2.0, su nombre, la clasificación/medallero, los puntos y si se permite descargar el cómputo
 *   general (PDF) allí (apagado por defecto). La visibilidad de cada prueba se decide en su detalle.
 * COLLABORATORS: AtletismoResultadosArea.jsx, hooks/useAthleticsResults.js.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/components/PublicationSettingsPanel.jsx
 */
import React, { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { FiInfo, FiSave } from "react-icons/fi";

import {
  useAthleticsResultsActions,
  useAthleticsSettings,
} from "../../../hooks/useAthleticsResults.js";
import { parseAthleticsError } from "../../../utils/athleticsResultsErrors.js";
import { formatDateTime } from "../labels.js";
import { Card, ErrorState, LoadingState, Spinner, Switch, primaryButtonClass } from "./ui.jsx";

const DEFAULT_LABEL = "Resultados en vivo";

const toForm = (settings) => ({
  publicTabEnabled: settings?.publicTabEnabled === true,
  publicTabLabel: settings?.publicTabLabel || DEFAULT_LABEL,
  publicStandingsEnabled: settings?.publicStandingsEnabled === true,
  publicPointsEnabled: settings?.publicPointsEnabled === true,
  publicStandingsPdfEnabled: settings?.publicStandingsPdfEnabled === true,
});

const PublicationSettingsPanel = ({ idevent, idsport, canWrite, publicEnabled }) => {
  const query = useAthleticsSettings({ idevent, idsport });
  const actions = useAthleticsResultsActions({ idevent, idsport });
  // Solo se guardan las ediciones locales; el resto se lee siempre de los ajustes del servidor. Así
  // el formulario nunca pinta un instante los valores por defecto antes de los reales.
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);

  const initial = useMemo(() => toForm(query.data), [query.data]);
  const form = useMemo(() => ({ ...initial, ...edits }), [initial, edits]);
  const changes = useMemo(() => {
    const out = {};
    Object.keys(form).forEach((key) => {
      const value = key === "publicTabLabel" ? form[key].trim() : form[key];
      if (value !== initial[key]) out[key] = value;
    });
    return out;
  }, [form, initial]);
  const dirty = Object.keys(changes).length > 0;
  const labelInvalid = !form.publicTabLabel.trim();

  const save = async () => {
    if (!dirty || labelInvalid) return;
    setSaving(true);
    try {
      await actions.saveSettings(changes);
      setEdits({});
      toast.success("Publicación actualizada.");
    } catch (error) {
      toast.error(parseAthleticsError(error).message);
    } finally {
      setSaving(false);
    }
  };

  const set = (key, value) => setEdits((current) => ({ ...current, [key]: value }));
  const disabled = !canWrite || saving;

  return (
    <Card className="mx-auto max-w-2xl overflow-hidden">
      <div className="border-b border-slate-100 px-5 py-4">
        <h3 className="text-lg font-bold text-indigo-700">Publicación en Hayllis 2.0</h3>
        <p className="mt-1 text-xs text-slate-500">
          Controla qué ve el público del atletismo de este evento. Cada prueba tiene además su propia
          visibilidad (lista de salida y resultados).
        </p>
      </div>

      {query.isLoading ? (
        <LoadingState label="Cargando ajustes..." />
      ) : query.isError ? (
        <ErrorState message={parseAthleticsError(query.error).message} onRetry={() => query.refetch()} />
      ) : (
        <div className="space-y-5 px-5 py-5">
          {!publicEnabled ? (
            <p className="flex items-start gap-2 rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600">
              <FiInfo className="mt-0.5 shrink-0" aria-hidden="true" />
              La publicación pública de atletismo está apagada en el servidor. Puedes dejar todo listo:
              se aplicará cuando se encienda.
            </p>
          ) : null}

          <Switch
            id="ath-public-tab"
            checked={form.publicTabEnabled}
            onChange={(value) => set("publicTabEnabled", value)}
            disabled={disabled}
            label="Mostrar pestaña en Hayllis 2.0"
          />

          <label htmlFor="ath-public-label" className="block">
            <span className="mb-1 block text-sm font-semibold text-slate-700">Nombre de la pestaña</span>
            <input
              id="ath-public-label"
              type="text"
              value={form.publicTabLabel}
              onChange={(event) => set("publicTabLabel", event.target.value)}
              disabled={disabled}
              maxLength={60}
              className={`w-full max-w-sm rounded-lg border px-3 py-2 text-sm font-semibold text-slate-800 outline-none transition focus:ring-2 disabled:bg-slate-50 ${
                labelInvalid
                  ? "border-rose-400 focus:border-rose-500 focus:ring-rose-100"
                  : "border-slate-300 focus:border-indigo-400 focus:ring-indigo-100"
              }`}
            />
            {labelInvalid ? (
              <span className="mt-1 block text-xs font-semibold text-rose-600">Escribe un nombre para la pestaña.</span>
            ) : null}
          </label>

          <Switch
            id="ath-public-standings"
            checked={form.publicStandingsEnabled}
            onChange={(value) => set("publicStandingsEnabled", value)}
            disabled={disabled}
            label="Mostrar clasificación y medallero"
          />

          <Switch
            id="ath-public-points"
            checked={form.publicPointsEnabled}
            onChange={(value) => set("publicPointsEnabled", value)}
            disabled={disabled}
            label="Mostrar puntos"
          />

          <div>
            <Switch
              id="ath-public-standings-pdf"
              checked={form.publicStandingsPdfEnabled}
              onChange={(value) => set("publicStandingsPdfEnabled", value)}
              disabled={disabled}
              label="Permitir descargar el cómputo general (PDF) en Hayllis 2.0"
            />
            <p className="mt-1 pl-14 text-xs text-slate-500">
              Apagado por defecto. El botón solo aparece en Hayllis 2.0 si la clasificación y los puntos también
              están publicados; el PDF público solo cuenta pruebas oficiales con resultados visibles.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
            <span className="text-[11px] text-slate-400">
              {query.data?.updatedAt ? `Última actualización: ${formatDateTime(query.data.updatedAt)}` : ""}
            </span>
            {canWrite ? (
              <button
                type="button"
                onClick={save}
                disabled={!dirty || labelInvalid || saving}
                className={primaryButtonClass}
              >
                {saving ? <Spinner /> : <FiSave aria-hidden="true" />}
                {saving ? "Guardando..." : "Guardar cambios"}
              </button>
            ) : (
              <span className="text-xs font-semibold text-slate-500">Tu rol solo permite consultar.</span>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

export default PublicationSettingsPanel;
